import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import type { z } from 'zod';
import { useChangePassword, useMe } from '../../api/auth';
import { applyApiError } from '../../components/form/apiErrors';
import { PasswordField } from '../../components/form/PasswordField';
import { changePassword } from './passwordSchemas';
import { formSaver, useUnsavedChanges } from '../../components/unsaved/unsaved';

type Values = z.infer<typeof changePassword>;

/** Used on the forced change-password page and under Settings, Your account. */
export function ChangePasswordForm({ onDone, submitLabel = 'Change password' }: { onDone(): void; submitLabel?: string }) {
  const change = useChangePassword();
  const { data: me } = useMe();
  const [error, setError] = useState('');
  const form = useForm<Values>({ resolver: zodResolver(changePassword), defaultValues: { currentPassword: '', newPassword: '', confirm: '' } });

  const submit = formSaver(form, async (v) => {
    setError('');
    try {
      await change.mutateAsync({ currentPassword: v.currentPassword, newPassword: v.newPassword });
      form.reset();
      onDone();
    } catch (e) {
      setError(applyApiError(e, form.setError, ['currentPassword', 'newPassword']));
      return false;
    }
  });
  useUnsavedChanges({ dirty: form.formState.isDirty, save: submit, discard: () => form.reset() });

  return (
    <Stack component="form" onSubmit={(e) => { e.preventDefault(); void submit(); }} noValidate spacing={2.5} sx={{ maxWidth: 420 }}>
      <input type="text" name="username" autoComplete="username" value={me?.username ?? me?.email ?? ''} hidden readOnly />
      <PasswordField control={form.control} name="currentPassword" label="Current password" autoComplete="current-password" />
      <PasswordField control={form.control} name="newPassword" label="New password" autoComplete="new-password" onGenerate={(p) => { form.setValue('newPassword', p, { shouldDirty: true, shouldValidate: true }); form.setValue('confirm', p, { shouldDirty: true, shouldValidate: true }); }} helperText="At least 10 characters" />
      <PasswordField control={form.control} name="confirm" label="Type it again" autoComplete="new-password" />
      {error && <Alert severity="error" role="alert">{error}</Alert>}
      <Button type="submit" variant="contained" disabled={change.isPending} sx={{ alignSelf: 'flex-start' }}>
        {change.isPending ? 'Saving…' : submitLabel}
      </Button>
      <Alert severity="info" icon={false} sx={{ color: 'text.secondary' }}>
        Changing your password signs you out on every other device.
      </Alert>
    </Stack>
  );
}
